import {OperatorFunction} from 'rxjs';
import {concatMap} from 'rxjs/operators';

import {
  fetchRawBootInboxesAndChannelsAsync,
  fetchRawBootTeammatesAsync,
} from '../../../srcCommon/services/boot/fetch/bootFetchHelpers';

/*
 * Types.
 */

export interface LegacyFrameRawBoot {
  isInitial: boolean;
  rawBoot: unknown;
}

/*
 * Api.
 */

export function toRawBootForLegacyFrame(
  companyUrl: string,
  hasLazyTeammates: boolean,
): OperatorFunction<LegacyFrameRawBoot, unknown> {
  return concatMap((rawBoot) => createRawBootForLegacyFrameAsync(rawBoot, companyUrl, hasLazyTeammates));
}

/*
 * Helpers.
 */

async function createRawBootForLegacyFrameAsync(
  legacyFrameRawBoot: LegacyFrameRawBoot,
  companyUrl: string,
  hasLazyTeammates: boolean,
): Promise<unknown> {
  const rawBoot = legacyFrameRawBoot.rawBoot as any;
  if (!legacyFrameRawBoot.isInitial) {
    // The legacy frame can just take the incremental boot and patch it into its boot.
    return rawBoot;
  }

  // Old Front needs the full raw boot initially, so let's fetch and patch in inboxes/channels and teammates.
  const [inboxesAndChannelsFetchResult, teammatesFetchResult] = await Promise.all([
    fetchRawBootInboxesAndChannelsAsync(companyUrl, false, 0, null),
    maybeFetchTeammates(companyUrl, hasLazyTeammates),
  ]);
  const {fetchedAt: inboxesAndChannelsFetchedAt, rawInboxes, rawChannels} = inboxesAndChannelsFetchResult;
  const {fetchedAt: teammatesFetchedAt, rawTeammates} = teammatesFetchResult;

  if (!hasLazyTeammates) {
    // If we don't have lazy teammates, only patch in inboxes/channels.
    return {
      ...rawBoot,
      ts: Math.min(rawBoot.ts, inboxesAndChannelsFetchedAt),
      inboxes: rawInboxes,
      channels: rawChannels,
    };
  }

  return {
    ...rawBoot,
    ts: Math.min(rawBoot.ts, inboxesAndChannelsFetchedAt, teammatesFetchedAt),
    inboxes: rawInboxes,
    channels: rawChannels,
    team: rawTeammates,
  };
}

async function maybeFetchTeammates(companyUrl: string, hasLazyTeammates: boolean) {
  if (!hasLazyTeammates) {
    return {
      fetchedAt: 0,
      rawTeammates: [],
    };
  }

  return fetchRawBootTeammatesAsync(companyUrl, 0, null);
}
